import reactUpdate from 'immutability-helper';
import moment from 'moment';
import {Dispatch} from 'react';
import isEqual from 'react-fast-compare';

import {open, SequenceAlignmentServerResponse} from 'analyses/alignment/AlignmentActions';
import {AlignmentAnalysis} from 'analyses/Analyses';
import {track} from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {NucleotideType} from 'biojs/BioConstants';
import {SequenceAnalysisSummary} from 'biojs/Sequence';
import {
  AlignmentProgram,
  BulkAlignmentCreationKind,
  ClustalOParameters,
  DNAAlignmentAnalysisJSONAttributes,
  DNAAlignmentKind,
  MafftParameters,
} from 'constants/AlignmentConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import FileStates, {InProgressAlignmentAttrs, SequenceFileState} from 'FileStates';
import {getDefaultSearchSearchParams} from 'nav/sidepanel/listing/SearchSearchParams';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import {pushToast} from 'nav/toasts/ToastActions';
import {ToastId, ToastType} from 'nav/toasts/ToastConstants';
import notifications, {FOREVER_DURATION, isResponse, Response} from 'notifications';
import {markEnd, markStart, start, stop} from 'performance/Perf';
import {getStore} from 'redux-stores/Store';
import {FilterKey, keyToEmptyFilter, SearchParams, SortKey} from 'search/SearchConstants';
import {Action} from 'tray/TrayTaskTypes';
import runModal from 'ui-library/runModal';
import {upload} from 'util/ImportUtil';
import {error} from 'util/Logging';
import {isJqXHR, postJSONPromise} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {Optional} from 'util/Types';

import CreateSequenceAlignmentsModal from './CreateSequenceAlignmentsModal';
import {
  AlignmentInput,
  AlignmentSetup,
  BioentityInput,
  CreateSequenceAlignmentModalAction,
  CreateSequenceAlignmentModalActionType,
  CreateSequenceAlignmentModalState,
  InputType,
  TextInput,
} from './createSequenceAlignmentsModalReducer';
import {
  getInputItemKey,
  getSelectedSetups,
  isBioentityInput,
  isTemplateSetup,
  requiresFolderId,
} from './SharedUtil';

interface MAFFTPayload {
  algorithmType: AlignmentProgram.MAFFT;
  parameters: MafftParameters;
}

interface ClustalOPayload {
  algorithmType: AlignmentProgram.CLUSTALO;
  parameters: ClustalOParameters;
}

type ProgramPayload = MAFFTPayload | ClustalOPayload;

interface AlignmentInputItemPayload {
  identifier: string;
  identifierType: InputType;
  useCurrentVersion?: boolean;
  originalFileURL?: string;
  nucleotideType: NucleotideType;
}

interface TemplateAlignmentPayload {
  alignmentType: DNAAlignmentKind.TEMPLATE;
  template: AlignmentInputItemPayload;
  items: Array<AlignmentInputItemPayload>;
}

interface ConsensusAlignmentPayload {
  alignmentType: DNAAlignmentKind.CONSENSUS;
  name: string;
  items: Array<AlignmentInputItemPayload>;
}

type AlignmentInputPayload = ConsensusAlignmentPayload | TemplateAlignmentPayload;

interface SequenceIdentifierContext {
  name: string;
  bases: string;
  alignmentRowNumber?: number;
}

interface SequenceIdentifierContextMap {
  [id: string]: SequenceIdentifierContext;
}

interface BulkAlignmentPayload {
  program: ProgramPayload;
  alignments: Array<AlignmentInputPayload>;
  folderId: Optional<string>;
  sequenceIdentifierToContext: SequenceIdentifierContextMap;
  disableCircularTemplateRotation: boolean;
}

export interface BulkAlignmentResponse {
  bulkAlignmentSummaries: Array<SequenceAnalysisSummary>;
}

interface RealignmentPayload {
  program: ProgramPayload;
  alignment: AlignmentInputPayload;
  sequenceAnalysisId: string;
  sequenceIdentifierToContext: SequenceIdentifierContextMap;
  disableCircularTemplateRotation: boolean;
}

function convertAlignmentInputToAlignmentInputItemPayload(
  item: AlignmentInput,
  isRealign: boolean
): AlignmentInputItemPayload {
  const payload: AlignmentInputItemPayload = {
    identifier: getInputItemKey(item),
    identifierType: item.type,
    nucleotideType: item.nucleotideType,
  };
  if (item.type === InputType.FILE) {
    payload.originalFileURL = item.originalFileURL;
  }
  if (isRealign && isBioentityInput(item)) {
    payload.useCurrentVersion = item.useCurrentVersion;
  }
  return payload;
}

interface FileParseFailure {
  error: string;
}

interface ParsedSequence {
  name: string;
  url: string;
}

interface FileParseSuccess {
  sequences: Array<ParsedSequence>;
}

interface UploadAndParseResult {
  sequences: Array<ParsedSequence>;
  originalFileUrl: string;
}

function isFileParseFailure(result: FileParseFailure | FileParseSuccess): result is FileParseFailure {
  return Boolean((result as FileParseFailure).error);
}

/**
 * Upload a file to s3 and then parse its contents into individual sequences.
 *
 * @param file: File to upload and parse.
 */
async function uploadAndParseFile(file: File): Promise<UploadAndParseResult | undefined> {
  let s3location: string;
  try {
    s3location = (await upload(file))!;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (res) {
    if (res) {
      res.userMessage =
        res.userMessage ?? res.responseJSON?.userMessage ?? `Failed to upload file "${file.name}".`;
    }
    notifications.notifyError(res, {duration: FOREVER_DURATION});
    // This is a special case where we want to capture a server error in Sentry because it likely came
    // from an error uploading to S3 - manually capture the response without throwing an error so we can
    // continue handling other file uploads.
    // TODO (BNCH-46793): Improve retries and error-handling in upload method
    if (isJqXHR(res)) {
      error(`Server [${res.status}]: ${res.responseText}`, {
        fingerprint: ['benchling', 'molbio', 'alignment-client-upload-failure', res.status.toString()],
      });
    }
    return;
  }
  const result: FileParseFailure | FileParseSuccess = await postJSONPromise({
    url: '/1/api/parse-sequence-file',
    data: {s3FileUrl: s3location},
  });
  if (isFileParseFailure(result)) {
    notifications.error(`Failed to parse file "${file.name}": ${result.error}.`, {
      duration: FOREVER_DURATION,
    });
    return;
  }
  return {
    sequences: result.sequences,
    originalFileUrl: s3location,
  };
}

/**
 * Uploads and parses a list of files and adds each parsed out sequence to the list of inputs.
 *
 * @param dispatch: Alignment modal actions dispatcher.
 * @param files: List of files to upload and parse.
 */
export async function uploadAndParseFiles(
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>,
  files: Array<File>
): Promise<void> {
  const numFiles = files.length;
  if (!numFiles) {
    return;
  }
  let doneCount: number = 0;
  let successCount: number = 0;
  const message = numFiles > 1 ? `Uploading ${numFiles} files` : `Uploading ${files[0].name}`;
  const toastId = pushToast({
    message,
    type: ToastType.INFO,
    duration: FOREVER_DURATION,
  });

  const promises = files.map(async (file, index) => {
    const result: UploadAndParseResult | undefined = await uploadAndParseFile(file);
    doneCount++;
    pushToast({id: toastId, message, progress: (doneCount / numFiles) * 100});
    if (!result) {
      dispatch({
        type: CreateSequenceAlignmentModalActionType.UPLOAD_FILE_FAILURE,
      });
      return;
    }
    successCount++;
    for (const {name, url} of result.sequences) {
      dispatch({
        type: CreateSequenceAlignmentModalActionType.ADD_INPUT,
        input: {
          type: InputType.FILE,
          name,
          originalFileURL: result.originalFileUrl,
          sequenceDictURL: url,
          nucleotideType: NucleotideType.DNA,
        },
      });
    }
    dispatch({
      type: CreateSequenceAlignmentModalActionType.UPLOAD_FILE_SUCCESS,
    });
  });

  await Promise.allSettled(promises);
  pushToast({
    id: toastId,
    message:
      successCount === 0 ? `All uploads failed` : `Successfully uploaded ${successCount} of ${numFiles}`,
    type: successCount === 0 ? ToastType.DANGER : ToastType.SUCCESS,
  });
}

/**
 * Sets the use current version flag for the given bioentity input in setups and configurations.
 * @param state Current state of the modal.
 * @param inputToUpdate Bioentity input to toggle the flag for.
 * @param value New value of the use current version flag to set.
 */
export function setUseCurrentVersion(
  state: CreateSequenceAlignmentModalState,
  inputToUpdate: BioentityInput,
  value: boolean
): CreateSequenceAlignmentModalState {
  const setUseCurrentVersionForInputs = (inputs: Array<AlignmentInput>): Array<AlignmentInput> =>
    inputs.map((input) => (isEqual(input, inputToUpdate) ? {...input, useCurrentVersion: value} : input));

  const setUseCurrentVersionForSetup = <T extends AlignmentSetup>(setup: T): T => {
    if (isTemplateSetup(setup)) {
      return {
        ...setup,
        template:
          isBioentityInput(setup.template) && isEqual(setup.template, inputToUpdate)
            ? {...setup.template, useCurrentVersion: value}
            : setup.template,
        items: setUseCurrentVersionForInputs(setup.items),
      };
    } else {
      return {
        ...setup,
        items: setUseCurrentVersionForInputs(setup.items),
      };
    }
  };

  return reactUpdate(state, {
    inputs: (inputs) => setUseCurrentVersionForInputs(inputs),
    multisequenceConfiguration: (setups) => setups.map((setup) => setUseCurrentVersionForSetup(setup)),
    pairwiseConfiguration: (setups) => setups.map((setup) => setUseCurrentVersionForSetup(setup)),
    consensusConfiguration: (setups) => setups.map((setup) => setUseCurrentVersionForSetup(setup)),
    realignmentInfo: (realignmentInfo) =>
      realignmentInfo
        ? {
            ...realignmentInfo,
            sequenceAnalysisInfo: {
              ...realignmentInfo.sequenceAnalysisInfo,
              alignmentSetup: setUseCurrentVersionForSetup(
                realignmentInfo.sequenceAnalysisInfo.alignmentSetup
              ),
            },
          }
        : undefined,
  });
}

/**
 * Removes the given input from all setups.
 *
 * If the input is the template for a setup, removes the entire setup.
 * @param state Current modal state.
 * @param inputToRemove Input to remove.
 */
export function removeInput(
  state: CreateSequenceAlignmentModalState,
  inputToRemove: AlignmentInput
): CreateSequenceAlignmentModalState {
  const removeInputItem = <T extends AlignmentSetup>(alignmentSetup: T): T => ({
    ...alignmentSetup,
    items: alignmentSetup.items.filter((input) => !isEqual(input, inputToRemove)),
  });

  return reactUpdate(state, {
    inputs: (input) => input.filter((input) => !isEqual(input, inputToRemove)),
    // Remove any alignment setup groups that use this input item as a template and filter alignment setup input lists.
    multisequenceConfiguration: (setups) =>
      setups.filter(({template}) => !isEqual(template, inputToRemove)).map((setup) => removeInputItem(setup)),
    pairwiseConfiguration: (setups) =>
      setups.filter(({template}) => !isEqual(template, inputToRemove)).map((setup) => removeInputItem(setup)),
    consensusConfiguration: (setups) => setups.map((setup) => removeInputItem(setup)),
    // Also remove the item from sequence analysis info
    // (templates are not handled because the template cannot be removed during realignment)
    realignmentInfo: (realignmentInfo) =>
      realignmentInfo
        ? {
            isTemplateSequenceEditable: realignmentInfo.isTemplateSequenceEditable,
            sequenceAnalysisInfo: {
              ...realignmentInfo.sequenceAnalysisInfo,
              alignmentSetup: removeInputItem(realignmentInfo.sequenceAnalysisInfo.alignmentSetup),
            },
          }
        : undefined,
  });
}

/**
 * Notify the user of successful completion a set of alignments with a link to open or list the alignments.
 * @param alignmentSummaries List of alignments that were completed.
 * @param toastId Toast id to use for displaying the success result.
 */
function notifyAlignmentSuccess(
  alignmentSummaries: Array<{sequence_id: string; apiId: string}>,
  toastId: ToastId
): void {
  let action: Action;
  let message: string;
  if (alignmentSummaries.length === 1) {
    const alignmentSummary = alignmentSummaries[0];
    const alignmentDetails = {
      sequenceId: alignmentSummary.sequence_id,
      alignmentId: alignmentSummary.apiId,
    };
    message = 'Alignment completed successfully.';
    action = {
      actionText: 'Open',
      onActionClick: () => {
        open(alignmentDetails);
      },
    };
  } else {
    message = 'Alignments completed successfully.';
    action = {
      actionText: 'View',
      onActionClick: () => {
        const searchParams: SearchParams = {
          ...getDefaultSearchSearchParams(),
          sorts: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
          filters: [
            {
              ...keyToEmptyFilter(FilterKey.TYPES),
              value: [ModelTypeConstants.SEQUENCE_ANALYSIS],
            },
            {
              ...keyToEmptyFilter(FilterKey.CREATED_AT),
              // YYYY-MM-DD in practice will be treated as midnight of the current day.
              // This is better than including the exact current time, since the alignments were created some
              // small time in the past.
              value: {from: moment().format('YYYY-MM-DD'), to: null},
            },
          ],
        };
        getStore().dispatch(SidepanelReduxActions.openListing(NavConstants.Section.SEARCH, searchParams));
      },
    };
  }
  pushToast({
    id: toastId,
    message,
    duration: 30,
    type: ToastType.SUCCESS,
    actions: [action],
  });
}

/**
 * Adds the newly created alignments to the alignment side panel for any open sequences.
 * @param alignmentSummaries Newly created alignment summaries.
 * @param toastId Id of the toast to use to notify the user.
 */
function onBulkAlignmentSuccess(alignmentSummaries: Array<SequenceAnalysisSummary>, toastId: ToastId): void {
  const fileStates = FileStates.get();
  alignmentSummaries.forEach((alignmentSummary: SequenceAnalysisSummary) => {
    const sequenceFileState = fileStates.get(alignmentSummary.sequence_id) as SequenceFileState | undefined;
    if (sequenceFileState) {
      const analyses = sequenceFileState.get('sequence').get('analyses');
      analyses.add(new AlignmentAnalysis(alignmentSummary, {parse: false}));
    }
  });
  notifyAlignmentSuccess(alignmentSummaries, toastId);
}

/**
 * Update the alignment view (if it's open) with the results of the realignment.
 * @param sequenceId Id of the sequence the alignment is associated with.
 * @param analysisAttributes Alignment analysis data.
 * @param toastId Id of the notification toast for this realignment process.
 */
function onRealignmentSuccess(
  sequenceId: string,
  analysisAttributes: DNAAlignmentAnalysisJSONAttributes,
  toastId: ToastId
): void {
  const fileStates = FileStates.get();
  const sequenceFileState = fileStates.get(sequenceId) as SequenceFileState | undefined;
  if (sequenceFileState) {
    const sequence = sequenceFileState.get('sequence');
    sequence.updateVersions({switchToLast: true});
    const analysis = sequence.get('analyses').get(analysisAttributes.id) as AlignmentAnalysis;
    if (analysis != null) {
      analysis.stopAllListeners();
      analysis.set(analysis.parse(analysisAttributes)!);
      analysis.registerListeners();
    }

    const tab = sequenceFileState.findAlignmentTab(analysis);
    if (tab) {
      tab.loadAnalysis(analysis);
      if (BenchlingConfig.MOLBIO_ENABLE_ALIGNMENT_UNDO && analysis) {
        // Ensure history is reset on realign
        tab.replaceHistory([
          {
            analysisState: analysis.toHistoryState(),
            selectionStart: 0,
            rowIndex: 0,
          },
        ]);
      }
    }
  }
  notifyAlignmentSuccess([{sequence_id: sequenceId, apiId: analysisAttributes.apiId}], toastId);
}

/**
 * Notify the user of a failure during the alignment with button to reopen the modal and adjust the parameters.
 * @param response Response from the server.
 * @param reducerState The original state of the modal.
 * @param toastId Id of the notification toast for this realignment process.
 */
function onAlignmentFailure(
  response: Response,
  reducerState: CreateSequenceAlignmentModalState,
  toastId: ToastId
): void {
  const message =
    (response && notifications.getFriendlyError(response)) ??
    'Sorry, an unexpected error occurred during the alignment operation.';
  pushToast({
    id: toastId,
    message,
    type: ToastType.DANGER,
    actions: [
      {
        actionText: 'Adjust input and retry',
        onActionClick: () => {
          track(reducerState.realignmentInfo ? 'realignment' : 'alignment', 'clickRetry');
          // Allow user to reopen modal with the current state on alignment failure for retrying or
          // adjusting input and parameters.
          runModal(CreateSequenceAlignmentsModal, {initialInputs: [], initialState: reducerState});
        },
      },
    ],
  });
}

/**
 * Get folder ID from alignment state if the selected alignment setup requires it. Otherwise return undefined.
 * @param state The current sequence alignment modal state.
 */
function getFolderIdPayload(state: CreateSequenceAlignmentModalState): string | undefined {
  if (
    getSelectedSetups(
      state.alignmentKind,
      state.pairwiseConfiguration,
      state.multisequenceConfiguration,
      state.consensusConfiguration
    ).some((setup) => requiresFolderId(setup))
  ) {
    assertAndContinue(state.folderId != null, 'Expected a folderId for current alignment setup.');
    return state.folderId!;
  }
  return undefined;
}

/**
 * Extract a json payload for the selected program and it's parameters from the current state.
 * @param state The current sequence alignment modal state.
 */
function getProgramPayload(state: CreateSequenceAlignmentModalState): ProgramPayload {
  let programPayload: ProgramPayload;
  if (state.program === AlignmentProgram.MAFFT) {
    programPayload = {
      algorithmType: state.program,
      parameters: state.mafftParameters,
    };
  } else {
    programPayload = {
      algorithmType: state.program,
      parameters: state.clustalOParameters,
    };
  }
  return programPayload;
}

/**
 * Extract a json payload for the sequence identifier context from the current state.
 * This is a mapping of id to name and bases for each "text" input.
 * @param state The current sequence alignment modal state.
 */
function getSequenceIdentifierToContext(
  state: CreateSequenceAlignmentModalState
): SequenceIdentifierContextMap {
  const allItems: Array<AlignmentInput> = [];

  if (state.alignmentKind === BulkAlignmentCreationKind.MULTISEQUENCE) {
    state.multisequenceConfiguration.forEach(({template, items}) => {
      allItems.push(template, ...items);
    });
  } else if (state.alignmentKind === BulkAlignmentCreationKind.PAIRWISE) {
    state.pairwiseConfiguration.forEach(({template, items}) => {
      allItems.push(template, ...items);
    });
  } else {
    state.consensusConfiguration.forEach(({items}) => {
      allItems.push(...items);
    });
  }

  return (
    allItems
      // only TEXT inputs go into the sequence identifier context.
      .filter(
        (alignmentInput: AlignmentInput): alignmentInput is TextInput =>
          alignmentInput.type === InputType.TEXT
      )
      .reduce((map: SequenceIdentifierContextMap, item: TextInput) => {
        const context: SequenceIdentifierContext = {
          name: item.name,
          bases: item.residues,
        };
        if (item.alignmentRowNumber !== undefined) {
          context.alignmentRowNumber = item.alignmentRowNumber;
        }
        map[item.id] = context;
        return map;
      }, {})
  );
}

/**
 * Extract a list of json payloads for alignments from the current state.
 * @param state The current sequence alignment modal state.
 */
function getAlignmentInputPayloads(state: CreateSequenceAlignmentModalState): Array<AlignmentInputPayload> {
  const alignmentInputs: Array<AlignmentInputPayload> = [];
  const isRealign = Boolean(state.realignmentInfo);

  if (state.alignmentKind === BulkAlignmentCreationKind.MULTISEQUENCE) {
    state.multisequenceConfiguration.forEach(({template, items}) =>
      alignmentInputs.push({
        alignmentType: DNAAlignmentKind.TEMPLATE,
        template: convertAlignmentInputToAlignmentInputItemPayload(template, isRealign),
        items: items.map((input) => convertAlignmentInputToAlignmentInputItemPayload(input, isRealign)),
      })
    );
  } else if (state.alignmentKind === BulkAlignmentCreationKind.PAIRWISE) {
    state.pairwiseConfiguration.forEach(({template, items}) => {
      items.forEach((input) => {
        alignmentInputs.push({
          alignmentType: DNAAlignmentKind.TEMPLATE,
          template: convertAlignmentInputToAlignmentInputItemPayload(template, isRealign),
          items: [convertAlignmentInputToAlignmentInputItemPayload(input, isRealign)],
        });
      });
    });
  } else {
    state.consensusConfiguration.forEach(({name, items}) =>
      alignmentInputs.push({
        alignmentType: DNAAlignmentKind.CONSENSUS,
        name,
        items: items.map((input) => convertAlignmentInputToAlignmentInputItemPayload(input, isRealign)),
      })
    );
  }
  return alignmentInputs;
}

/**
 * Promise wrapper that manages an in-progress alignment entry in the alignment side panel for the given sequence.
 * @param promise Alignment promise to wrap.
 * @param sequenceId Id of the sequence the alignment is associated with.
 * @param properties Properties for displaying on the in-progress alignment entry.
 */
function withPendingStatus<T>(
  promise: Promise<T>,
  sequenceId: string,
  properties: Partial<InProgressAlignmentAttrs>
): Promise<T> {
  return (
    FileStates.get()
      .get(sequenceId)
      ?.get('toolbar')
      ?.get('alignmentPanelState')
      ?.withAlignmentPending(promise, properties) || promise
  );
}

enum ExecutionType {
  MONOLITH = 'MONOLITH',
  JEF = 'JEF',
}

/**
 * Perform a bulk set of alignments using the inputs from the current state.
 *
 * This includes:
 *  * Notifying the user of the submission via toast.
 *  * Posting the alignment data to the backend and handling the response async.
 *  * Register the alignments as in progress with any currently open template sequences.
 * @param state The current sequence alignment modal state.
 */
export async function performBulkAlignment(state: CreateSequenceAlignmentModalState): Promise<void> {
  const performanceId = start('alignmentCreation', {enabled: false}, undefined, {
    program: state.program,
    alignmentKind: state.alignmentKind,
    executionType:
      BenchlingConfig.MOLBIO_ENABLE_JEF_MAFFT_ALIGNMENTS && state.program === AlignmentProgram.MAFFT
        ? ExecutionType.JEF
        : ExecutionType.MONOLITH,
  });
  markStart(performanceId);
  const bulkAlignPayload: BulkAlignmentPayload = {
    program: getProgramPayload(state),
    alignments: getAlignmentInputPayloads(state),
    folderId: getFolderIdPayload(state),
    sequenceIdentifierToContext: getSequenceIdentifierToContext(state),
    disableCircularTemplateRotation: state.disableCircularTemplateRotation,
  };
  const alignmentCount = bulkAlignPayload.alignments.length;
  const toastId = pushToast({
    message: `Performing ${alignmentCount} ${pluralize(alignmentCount, 'alignment')}.`,
    type: ToastType.INFO,
    duration: FOREVER_DURATION,
  });
  const trackMetadata = {count: alignmentCount, kind: state.alignmentKind};

  try {
    let alignmentPromise: Promise<BulkAlignmentResponse> = TaskRunner.postJSONPromise(
      {
        url: `/1/api/bulk-align`,
        data: bulkAlignPayload,
      },
      BenchlingConfig.MOLBIO_ALIGNMENT_TIMEOUT * 1000
    );
    // For any open template sequences, register all alignments in the alignment panel to indicate that they are in-progress.
    bulkAlignPayload.alignments
      .filter((value): value is TemplateAlignmentPayload => value.alignmentType === DNAAlignmentKind.TEMPLATE)
      .filter(({template}) => template.identifierType === InputType.BIOENTITY)
      .forEach(({template, items}) => {
        alignmentPromise = withPendingStatus(alignmentPromise, template.identifier, {
          algorithm: state.program,
          numSequences: items.length + 1,
        });
      });
    const {bulkAlignmentSummaries} = await alignmentPromise;
    track('alignment', 'success', trackMetadata);
    onBulkAlignmentSuccess(bulkAlignmentSummaries, toastId);
  } catch (e) {
    track('alignment', 'failure', trackMetadata);
    if (isResponse(e)) {
      onAlignmentFailure(e, state, toastId);
    } else {
      throw e;
    }
  } finally {
    markEnd(performanceId);
    stop(performanceId);
  }
}

/**
 * Perform a realignment using the inputs from the current state.
 *
 * This includes:
 *  * Notifying the user of the submission via toast.
 *  * Posting the alignment data to the backend and handling the response async.
 *  * Register the alignments as in progress with any currently open template sequences.
 * @param state The current sequence alignment modal state.
 */
export async function performRealignment(state: CreateSequenceAlignmentModalState): Promise<void> {
  const performanceId = start('realignment', {enabled: false}, undefined, {
    program: state.program,
    alignmentKind: state.alignmentKind,
  });
  markStart(performanceId);
  const alignmentInputs = getAlignmentInputPayloads(state);
  // This should not be allowed.
  if (alignmentInputs.length !== 1) {
    throw new Error('Attempted realignment for multiple alignments.');
  }

  const realignPayload: RealignmentPayload = {
    program: getProgramPayload(state),
    alignment: alignmentInputs[0],
    sequenceAnalysisId: state.realignmentInfo!.sequenceAnalysisInfo.sequenceAnalysisId,
    sequenceIdentifierToContext: getSequenceIdentifierToContext(state),
    disableCircularTemplateRotation: state.disableCircularTemplateRotation,
  };
  const trackMetadata = {kind: state.alignmentKind};
  const toastId = pushToast({
    message: `Performing realignment.`,
    type: ToastType.INFO,
    duration: FOREVER_DURATION,
  });

  try {
    let alignmentPromise: Promise<SequenceAlignmentServerResponse> = TaskRunner.postJSONPromise(
      {
        url: `/1/api/realignment`,
        data: realignPayload,
      },
      BenchlingConfig.MOLBIO_ALIGNMENT_TIMEOUT * 1000
    );
    const {items} = realignPayload.alignment;
    alignmentPromise = withPendingStatus(
      alignmentPromise,
      state.realignmentInfo!.sequenceAnalysisInfo.sequenceId,
      {
        algorithm: state.program,
        numSequences: items.length + 1,
      }
    );

    const {sequence, analysis} = await alignmentPromise;
    onRealignmentSuccess(sequence.id, analysis, toastId);
    track('realignment', 'success', trackMetadata);
  } catch (e) {
    track('realignment', 'failure', trackMetadata);
    if (isResponse(e)) {
      onAlignmentFailure(e, state, toastId);
    } else {
      throw e;
    }
  } finally {
    markEnd(performanceId);
    stop(performanceId);
  }
}
